import React, { PureComponent, ReactNode, useState } from 'react';
import { generateDySecId } from '../../store/utils';
import { PadDForm, PadFDFormElement } from '../../api/pushVisit';
import { getGById } from '../../store/idbHandler';
import { GDyForm } from '../../store/db';
import onContainerClick from '../../pages/prescriptionPad/onClickContainer';
import { Modal, notification } from 'antd';
import WithBodyClickClose from './WithBodyClickClose';
import { escTextIcon } from './Icons';
import { DB_TABLES } from '../../store/dbTables';
import { getMyDbId } from '../../api/mactiveid';
import { GenieMemoryCustomItem } from '../../api/pushGenie';

type Props = {
    isPrintEnabled: boolean;
    form: PadDForm;
    onPrintChange: (value: boolean) => void;
    onUpdate: (name: string, elements?: PadFDFormElement[]) => void;
    onDelete?: () => void;
    customSectionsConfig?: GenieMemoryCustomItem[];
};

type State = {
    isSectionNameEdit: boolean;

    suggestions?: PadFDFormElement[];
    suggestionIndex?: number;
};

export default class DynamicCard extends PureComponent<Props, State> {
    nameInputRef: React.RefObject<HTMLInputElement> = React.createRef();
    newE = {
        id: generateDySecId(),
    };
    getFormFromProps = (): PadDForm => {
        const { form } = this.props;
        const elements = form.elements || [];
        return {
            id: form.id,
            name: form.name,
            elements: elements.length ? elements : [{ id: generateDySecId() }],
        };
    };

    state: State = {
        isSectionNameEdit: !this.props.form.name,
    };

    setSuggestionVisible = (index?: number): void => {
        this.setState({ suggestionIndex: index });
    };

    componentDidMount = async (): Promise<void> => {
        const gDForm = await getGById<GDyForm>(
            getMyDbId() || '',
            DB_TABLES.dyForm,
            this.props.form.id,
        );
        this.setState({
            suggestions: gDForm?.elements,
        });
    };

    componentDidUpdate = (): void => {
        if (!this.props.form.elements)
            this.newE = {
                id: generateDySecId(),
            };
    };

    onEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            this.onSectionNameDone();
        }
    };

    onSectionNameDone = (): void => {
        const { onUpdate, form } = this.props;

        if (this.nameInputRef?.current?.value.trim()) {
            this.setState({ isSectionNameEdit: false });
            onUpdate(this.nameInputRef?.current?.value || '', [...(form.elements || [])]);
        } else {
            notification.error({
                message: 'Section name cannot be empty',
                duration: 5,
            });
        }
    };

    onSectionNameEdit = (): void => {
        this.setState({
            isSectionNameEdit: true,
        });
    };

    onSubSectionTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const index = parseInt(e.target.getAttribute('index') || '');
        const { onUpdate, form } = this.props;

        if (isNaN(index)) {
            return;
        }

        let element: PadFDFormElement | undefined = form.elements && form.elements[index];

        if (!element) {
            element = { id: e.target.getAttribute('e_id') || generateDySecId() };
            if (form.elements) {
                form.elements[index] = element;
            } else {
                form.elements = [element];
            }
        }

        element.name = e.target.value;

        onUpdate(this.props.form.name, [...(this.props.form.elements || [])]);
    };

    onDeleteElement = (e: React.MouseEvent<HTMLDivElement>): void => {
        this.newE = {
            id: generateDySecId(),
        };

        const index = parseInt(e.currentTarget.getAttribute('index') || '');

        const { onUpdate, form } = this.props;

        const element: PadFDFormElement = (form.elements && form.elements[index]) || {
            id: generateDySecId(),
        };

        if (isNaN(index) || !element) {
            return;
        }

        const updatedElement = form.elements;

        updatedElement?.splice(index, 1);

        onUpdate(this.props.form.name, [...(updatedElement || [])]);
    };

    onSubSectionTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const index = parseInt(e.target.getAttribute('index') || '');
        const { onUpdate, form } = this.props;

        if (isNaN(index)) {
            return;
        }

        let element: PadFDFormElement | undefined = form.elements && form.elements[index];

        if (!element) {
            element = { id: e.target.getAttribute('e_id') || generateDySecId() };
            if (form.elements) {
                form.elements[index] = element;
            } else {
                form.elements = [element];
            }
        }

        element.value = e.target.value;

        onUpdate(this.props.form.name, [...(this.props.form.elements || [])]);
    };

    onSuggestionSelect = (index: number, el: PadFDFormElement): void => {
        const { onUpdate, form } = this.props;
        const element: PadFDFormElement = (form.elements && form.elements[index]) || {
            id: generateDySecId(),
        };

        if (isNaN(index) || !element) {
            return;
        }

        const updatedElement = form.elements || [];

        updatedElement[index] = { ...el };

        onUpdate(this.props.form.name, [...(updatedElement || [])]);
    };

    addNewSubSection = (): void => {
        const { onUpdate, form } = this.props;

        const element: PadFDFormElement = {
            id: generateDySecId(),
        };

        if (form.elements) {
            form.elements.push(element);
        } else {
            form.elements = [element];
        }

        onUpdate(this.props.form.name, [...(form.elements || [])]);

        setTimeout(() => {
            document.getElementById(generateSectionTitleInputId(element.id))?.focus();
        }, 200);
    };

    onDelete = (): void => {
        const { onDelete } = this.props;

        if (!onDelete) {
            return;
        }

        Modal.warn({
            title: 'Are you sure, you want to delete?',
            onOk: onDelete,
            okText: 'Yes',
            closable: true,
            okButtonProps: { className: 'primary-btn font-lato' },
            cancelButtonProps: { className: 'neutral-btn font-lato' },
        });

        setTimeout(() => {
            const el: HTMLDivElement = document.getElementsByClassName(
                'ant-modal-wrap',
            )[0] as HTMLDivElement;
            el?.focus();
        }, 200);
    };

    render(): ReactNode {
        const { isSectionNameEdit, suggestions, suggestionIndex } = this.state;
        const { onDelete, isPrintEnabled, form, onPrintChange } = this.props;
        const { id, name, elements: formElements } = form || {};

        const elements = !formElements || formElements.length === 0 ? [this.newE] : formElements;

        const selectedIdsMap = new Set(elements.map((el) => el.id));

        return (
            <div id={id} onFocus={onContainerClick} className="rounded-8 bg-white focusable row">
                <div className="flex items-center justify-between w-full px-16">
                    <div className="flex items-center space-x-10">
                        <div className="rounded-4 my-10 w-26 h-26 bg-darwin-neutral-600 flex items-center justify-center">
                            <svg
                                className="fill-white"
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 13 13"
                            >
                                <path d="M12.0752 1.83594L11.1846 0.945312C10.9033 0.664062 10.5049 0.5 10.1299 0.5C9.75488 0.5 9.35645 0.664062 9.0752 0.945312L1.24707 8.77344C1.17676 8.82031 1.15332 8.89062 1.12988 8.96094L0.520508 12.0312C0.473633 12.2891 0.661133 12.5 0.87207 12.5C0.895508 12.5 0.942383 12.5 0.96582 12.5L4.03613 11.8906C4.10645 11.8672 4.17676 11.8438 4.24707 11.7734L12.0752 3.94531C12.6611 3.35938 12.6611 2.42188 12.0752 1.83594ZM3.59082 10.8359L1.83301 11.1875L2.18457 9.42969L2.77051 8.84375V10.25H4.17676L3.59082 10.8359ZM3.89551 9.125V7.71875L8.02051 3.59375L9.4502 5L5.30176 9.125H3.89551Z" />
                            </svg>
                        </div>
                        {isSectionNameEdit ? (
                            <div className="flex justify-between items-center rounded-t-8 py-8">
                                <div className="bg-white flex items-center justify-between px-8 py-4 rounded-6 max-w-462 w-full mr-8 border border-seperator-dark">
                                    <input
                                        ref={this.nameInputRef}
                                        className="w-full font-700 placeholder-neutral-6 text-neutral-black text-14"
                                        placeholder="Add note title here"
                                        defaultValue={name}
                                        onKeyDown={this.onEnter}
                                        autoFocus
                                        onBlur={this.onSectionNameDone}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center rounded-t-8">
                                <div className="flex items-center w-full space-x-8">
                                    <p className="text-neutral-1 text-16 font-700 cursor-pointer">
                                        {name || ''}
                                    </p>
                                    <div
                                        onClick={this.onSectionNameEdit}
                                        className="flex items-center justify-center w-16 h-16 cursor-pointer"
                                    >
                                        <svg
                                            className="w-16 h-16 fill-neutral-1"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M495.6 49.23l-32.82-32.82C451.8 5.471 437.5 0 423.1 0c-14.33 0-28.66 5.469-39.6 16.41L167.5 232.5C159.1 240 154.8 249.5 152.4 259.8L128.3 367.2C126.5 376.1 133.4 384 141.1 384c.916 0 1.852-.0918 2.797-.2813c0 0 74.03-15.71 107.4-23.56c10.1-2.377 19.13-7.459 26.46-14.79l217-217C517.5 106.5 517.4 71.1 495.6 49.23zM461.7 94.4L244.7 311.4C243.6 312.5 242.5 313.1 241.2 313.4c-13.7 3.227-34.65 7.857-54.3 12.14l12.41-55.2C199.6 268.9 200.3 267.5 201.4 266.5l216.1-216.1C419.4 48.41 421.6 48 423.1 48s3.715 .4062 5.65 2.342l32.82 32.83C464.8 86.34 464.8 91.27 461.7 94.4zM424 288c-13.25 0-24 10.75-24 24v128c0 13.23-10.78 24-24 24h-304c-13.22 0-24-10.77-24-24v-304c0-13.23 10.78-24 24-24h144c13.25 0 24-10.75 24-24S229.3 64 216 64L71.1 63.99C32.31 63.99 0 96.29 0 135.1v304C0 479.7 32.31 512 71.1 512h303.1c39.69 0 71.1-32.3 71.1-72L448 312C448 298.8 437.3 288 424 288z"></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center">
                        <div className="flex justify-end items-center rounded-b-8">
                            <div
                                onClick={() => onPrintChange(!isPrintEnabled)}
                                className="space-x-8 flex items-center mr-12"
                            >
                                <p className="text-neutral-black whitespace-nowrap text-14">
                                    Print on prescription
                                </p>
                                <div className="switch">
                                    <input type="checkbox" checked={isPrintEnabled} />
                                    <span className="slider" />
                                </div>
                            </div>
                        </div>
                        {onDelete && (
                            <div className="flex items-center justify-end">
                                <div onClick={this.onDelete} className="w-16 h-16 cursor-pointer">
                                    <svg
                                        className="w-16 h-16 fill-neutral-1"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512"
                                    >
                                        <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`flex-1 p-16 pt-0 space-y-12 ${!name ? 'opacity-50' : ''}`}>
                    {elements &&
                        elements
                            .slice(0, 10)
                            .map((e, index) => (
                                <SectionElement
                                    e={e}
                                    isDisabled={!name}
                                    index={index}
                                    suggestions={suggestions?.filter(
                                        (t) => !selectedIdsMap.has(t.id),
                                    )}
                                    onDeleteElement={this.onDeleteElement}
                                    onSubSectionTextChange={this.onSubSectionTextChange}
                                    onSubSectionTitleChange={this.onSubSectionTitleChange}
                                    onSuggestionSelect={(el) => this.onSuggestionSelect(index, el)}
                                    suggestionVisible={suggestionIndex === index}
                                    setSuggestionVisible={(value) =>
                                        this.setSuggestionVisible(value ? index : undefined)
                                    }
                                    customSectionsConfig={this.props.customSectionsConfig}
                                    id={this.props.form.id}
                                />
                            ))}
                    <div
                        onClick={this.addNewSubSection}
                        className="flex items-center w-fit space-x-8 cursor-pointer"
                    >
                        <div className="rounded-full w-24 h-24 min-w-24 min-h-24 flex cursor-pointer items-center justify-center">
                            <svg
                                className="w-16 h-16 fill-actionPrimary"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path d="M432 256C432 269.3 421.3 280 408 280h-160v160c0 13.25-10.75 24.01-24 24.01S200 453.3 200 440v-160h-160c-13.25 0-24-10.74-24-23.99C16 242.8 26.75 232 40 232h160v-160c0-13.25 10.75-23.99 24-23.99S248 58.75 248 72v160h160C421.3 232 432 242.8 432 256z"></path>
                            </svg>
                        </div>
                        <p className="text-actionPrimary font-700 text-14 cursor-pointer">
                            Add another row..
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

const generateSectionTitleInputId = (id?: string) => `st-${id}`;

const SectionElement: React.FC<{
    e: { id?: string | undefined; name?: string | undefined; value?: string | undefined };
    isDisabled: boolean;
    index: number;
    onSubSectionTitleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onSubSectionTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onDeleteElement: (e: React.MouseEvent<HTMLDivElement>) => void;
    onSuggestionSelect: (el: PadFDFormElement) => void;
    suggestions?: PadFDFormElement[];
    suggestionVisible: boolean;
    setSuggestionVisible: (suggestionVisible: boolean) => void;
    autoFocusTitle?: boolean;
    customSectionsConfig?: GenieMemoryCustomItem[];
    id?: string;
}> = ({
    e,
    isDisabled,
    index,
    suggestions,
    onDeleteElement,
    onSubSectionTitleChange,
    onSubSectionTextChange,
    onSuggestionSelect,
    suggestionVisible,
    setSuggestionVisible,
    autoFocusTitle,
    customSectionsConfig = [],
    id,
}): JSX.Element => {
    const [titleValue, setTitleValue] = useState(e.name);
    const escIcon = escTextIcon('h-20 w-20');
    const sectionConfig = customSectionsConfig.find((c) => c.sectionId === id);
    const isSectionEnabled = sectionConfig?.isSectionEnabled ?? true;
    const rememberNotes = sectionConfig?.rememberNotes ?? true;
    const showName = isSectionEnabled;
    const showValue = isSectionEnabled && rememberNotes;

    const renderSuggestions = !titleValue
        ? suggestions
        : suggestions?.filter((s) =>
              s.name?.toLowerCase()?.includes(titleValue?.toLowerCase() || ''),
          );

    const filterSectionSuggestion = (el: PadFDFormElement): PadFDFormElement => {
        const clone = { ...el };
        if (!showName) {
            clone.name = undefined;
        }
        if (!showValue) {
            clone.value = undefined;
        }
        return clone;
    };

    return (
        <div
            key={`${index}-${e.id}`}
            className="dynamic-section-row"
            onClick={() => {
                if (isDisabled) {
                    notification.error({
                        message: 'Please enter a title.',
                        duration: 5,
                        placement: 'topRight',
                    });
                }
            }}
        >
            <div className="sub-heading">
                <textarea
                    disabled={isDisabled}
                    id={generateSectionTitleInputId(e.id)}
                    autoFocus={autoFocusTitle}
                    defaultValue={e.name}
                    index={index.toString()}
                    e_id={e.id}
                    placeholder="Type sub-section..."
                    onBlur={onSubSectionTitleChange}
                    onFocus={() => setSuggestionVisible(true)}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSuggestionVisible(true);
                    }}
                    onKeyDown={(e) => {
                        e.key === 'Escape' && setSuggestionVisible(false);
                    }}
                    spellCheck={true}
                    onChange={(e) => setTitleValue(e.target.value)}
                />
                <div className="sub-heading-mimic"></div>
            </div>
            <div className="sub-heading-details">
                <textarea
                    disabled={isDisabled}
                    rows={3}
                    placeholder="Add detailed notes.."
                    index={index.toString()}
                    e_id={e.id}
                    onBlur={onSubSectionTextChange}
                    defaultValue={e.value}
                    spellCheck={true}
                />
                {(index !== 0 || e.name || e.value) && (
                    <div
                        index={index.toString()}
                        onClick={onDeleteElement}
                        className="flex items-center justify-center w-24 h-24 min-w-24 min-h-24 rounded-full absolute -right-12 -top-12 cursor-pointer"
                    >
                        <svg
                            className="w-12 h-12 fill-textColor-secondary"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                        >
                            <path d="M424 80h-74.38l-34-56.75C306.9 8.875 291.3 0 274.4 0H173.6c-16.88 0-32.5 8.875-41.25 23.25L98.38 80H23.1C10.75 80 0 90.75 0 103.1S10.75 128 23.1 128H32l21.21 339c1.5 25.25 22.52 45 47.9 45h245.8c25.38 0 46.4-19.75 47.9-45L416 128h8C437.3 128 448 117.3 448 104S437.3 80 424 80zM173.6 48h100.8l19.25 32H154.4L173.6 48zM346.9 464H101.1L80.13 128h287.8L346.9 464z"></path>
                        </svg>
                    </div>
                )}
            </div>
            {suggestionVisible &&
                isSectionEnabled &&
                renderSuggestions &&
                renderSuggestions?.length > 0 && (
                    <WithBodyClickClose closeMe={() => setSuggestionVisible(false)}>
                        <div id="dialog" className="autosuggest" style={{ width: '100%' }}>
                            <div className="flex items-center justify-between">
                                <p className="text-neutral-4 text-10">
                                    Select an option or create one
                                </p>
                                <div className="flex items-center space-x-4">
                                    {escIcon}

                                    <p className="text-neutral-1 body-3">Close</p>
                                </div>
                            </div>
                            <div>
                                {renderSuggestions
                                    ?.filter((rs) => rs.name || showValue)
                                    .map((rs, index) => (
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSuggestionSelect(
                                                    filterSectionSuggestion({ ...rs }),
                                                );
                                                setSuggestionVisible(false);
                                            }}
                                            className={`flex items-start py-12 cursor-pointer ${
                                                index !== renderSuggestions.length - 1
                                                    ? 'border-b border-seperator-dark'
                                                    : ''
                                            }`}
                                        >
                                            <p className="heading">{rs.name || ''}</p>
                                            {showValue && (
                                                <p className=" heading-details truncate-overflow-2">
                                                    {rs.value || ''}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </WithBodyClickClose>
                )}
        </div>
    );
};
